/**
 * useAIConsent Hook
 * Manages AI data sharing consent state for Apple App Store compliance (Guideline 5.1.2(i))
 *
 * Apple requires explicit user consent before sharing personal data with third-party AI services.
 * Consent is synced to the server (blocking API call) and enforced server-side.
 * Local cache in app gates enables fast startup.
 *
 * **State is held in `useAIConsentStore`** (a Zustand store) rather than
 * in this hook's local `useState`. The store is the single source of
 * truth — every `useAIConsent` consumer subscribes to it, so mutations
 * propagate to all mounted components without remounting or reloading.
 * See the store file for the full rationale.
 *
 * This file is now a thin compatibility wrapper that:
 *   - hydrates the store on first mount (idempotent)
 *   - selects the fields callers expect
 *   - owns the disclosed-provider list + copy shared by every disclosure surface
 */

import { useEffect, useMemo } from 'react';
import type { TFunction } from 'i18next';
import {
  useAIConsentStore,
  CURRENT_CONSENT_VERSION,
} from '../store/aiConsentStore';
import aiProviders from '../generated/shared/aiProviders.json';

/**
 * The third-party AI providers Fluencia sends user data to. These MUST be
 * disclosed to users before consent (Apple Guideline 5.1.2(i), GDPR Art. 13).
 *
 * The disclosed *set* is defined once in `/shared/aiProviders.json` so the
 * backend can assert that every provider it is actually configured to call
 * (AI_PROVIDER / AI_FALLBACK_PROVIDER) appears here — see
 * backend/src/__tests__/unit/aiProviderDisclosure.test.ts. That check is what
 * catches the drift this list suffered when the primary LLM flipped from
 * Gemini to OpenAI (PR #82) and the disclosure never followed.
 */
export type AIProviderId =
  | keyof typeof aiProviders.llm
  | keyof typeof aiProviders.speech;

/**
 * Display order for every disclosure surface: language models first (they
 * receive message text), speech last. Derived from the shared manifest so the
 * consent modal and the Profile summary can never list different providers.
 */
export const AI_PROVIDERS: readonly AIProviderId[] = [
  ...(Object.keys(aiProviders.llm) as (keyof typeof aiProviders.llm)[]),
  ...(Object.keys(aiProviders.speech) as (keyof typeof aiProviders.speech)[]),
];

interface ProviderDisclosure {
  name: string;
  purpose: string;
  dataShared: string;
}

/**
 * Resolve a provider's disclosure copy. Shared by every surface that names
 * providers (consent modal, Profile summary) so they can't drift apart.
 *
 * The switch carries its weight twice over, so keep the shape:
 *   - every key is a string literal, as `literal-i18n-keys` (and the
 *     `CustomTypeOptions` key typing) require — a lookup like `t(keys[id])`
 *     is an ESLint error, not just a style preference;
 *   - it is exhaustive over `AIProviderId` with a declared return type and no
 *     `default`, so adding a provider to /shared/aiProviders.json fails the
 *     type-check here ("lacks ending return statement") until its copy exists.
 */
export function translateProvider(t: TFunction, id: AIProviderId): ProviderDisclosure {
  switch (id) {
    case 'gemini':
      return {
        name: t('chat.consent.providers.geminiName'),
        purpose: t('chat.consent.providers.geminiPurpose'),
        dataShared: t('chat.consent.providers.geminiDataShared'),
      };
    case 'openai':
      return {
        name: t('chat.consent.providers.openaiName'),
        purpose: t('chat.consent.providers.openaiPurpose'),
        dataShared: t('chat.consent.providers.openaiDataShared'),
      };
    case 'anthropic':
      return {
        name: t('chat.consent.providers.anthropicName'),
        purpose: t('chat.consent.providers.anthropicPurpose'),
        dataShared: t('chat.consent.providers.anthropicDataShared'),
      };
    case 'azure':
      return {
        name: t('chat.consent.providers.azureName'),
        purpose: t('chat.consent.providers.azurePurpose'),
        dataShared: t('chat.consent.providers.azureDataShared'),
      };
  }
}

interface AIConsentState {
  granted: boolean;
  grantedAt: string | null;
  version: number;
}

interface UseAIConsentResult {
  hasConsent: boolean;
  isLoading: boolean;
  consentData: AIConsentState | null;
  /**
   * Returns `true` when consent successfully synced to the server,
   * `false` when the sync failed (the store also surfaces an error
   * banner in that case). Callers that need to proceed only on
   * success — e.g. closing a modal + navigating into a paid
   * surface — must check the return value, otherwise the surface
   * they navigate to will likely 403 on the next consent-gated call.
   */
  grantConsent: () => Promise<boolean>;
  revokeConsent: () => Promise<void>;
  needsReConsent: boolean;
  refreshConsent: () => Promise<void>;
}

export function useAIConsent(): UseAIConsentResult {
  // Subscribe to individual store slices so the hook only re-renders
  // when fields it actually returns change.
  const granted = useAIConsentStore((s) => s.granted);
  const grantedAt = useAIConsentStore((s) => s.grantedAt);
  const version = useAIConsentStore((s) => s.version);
  const isLoading = useAIConsentStore((s) => s.isLoading);
  const grantConsent = useAIConsentStore((s) => s.grantConsent);
  const revokeConsent = useAIConsentStore((s) => s.revokeConsent);
  const hydrate = useAIConsentStore((s) => s.hydrate);

  // Hydrate on first mount. Idempotent — subsequent mounts of other
  // `useAIConsent` instances also call this, but the store's hydrate
  // is just a cache read so re-running it is cheap and keeps the
  // store in sync if the cache was mutated by another part of the app
  // (e.g. fetchProfile re-saving the cache after a /me round-trip).
  useEffect(() => {
    hydrate();
  }, [hydrate]);

  // `consentData` preserves the legacy shape (was `AIConsentState | null`
  // where null meant "not granted"). The store distinguishes null
  // (loading) from false (revoked) for internal clarity; surface
  // the legacy contract to callers.
  const consentData = useMemo<AIConsentState | null>(() => {
    if (granted !== true) return null;
    return {
      granted: true,
      grantedAt,
      version,
    };
  }, [granted, grantedAt, version]);

  // `needsReConsent` = user consented at some point, but the current
  // version differs (we added/removed providers). The modal needs to
  // be shown again to re-collect consent for the new set.
  const needsReConsent = useMemo(() => {
    if (granted !== true) return false;
    return version !== CURRENT_CONSENT_VERSION;
  }, [granted, version]);

  return {
    hasConsent: granted === true,
    isLoading,
    consentData,
    grantConsent,
    revokeConsent,
    needsReConsent,
    // `refreshConsent` was previously the cache-reload path; now it's
    // just the store's hydrate. Preserved for callers that want to
    // force a re-read (e.g. after an external mutation).
    refreshConsent: hydrate,
  };
}
